/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAccount } from "wagmi";
import type {
  UseStakingSubgraphResult,
  VaultStats,
  UserStakingData,
  StakingActivity,
  StakingTransaction,
} from "@/types/staking";
import {
  getStakingVaultData,
  getUserStakingActivity,
  type StakingVaultData,
  type StakingTransaction as SubgraphStakingTransaction,
} from "@/lib/bridge/subgraph";

// Data transformation functions
const transformVaultData = (
  vaultData: StakingVaultData | null,
): VaultStats | null => {
  if (!vaultData) return null;

  // Convert basis points to percentage (divide by 10000)
  // e.g., 19,198,401 basis points = 1,919.84% APR
  const aprFromBasisPoints = parseFloat(vaultData.currentAPR) / 10000;
  const apyFromBasisPoints = parseFloat(vaultData.currentAPY) / 10000;

  return {
    totalStaked: vaultData.totalSupply,
    totalSupply: vaultData.totalSupply,
    rewardRate: vaultData.rewardRate,
    rewardsDuration: parseInt(vaultData.rewardsDuration),
    periodFinish: parseInt(vaultData.periodFinish) * 1000, // Convert to milliseconds
    lastUpdateTime: Date.now() - 1000, // Approximate
    rewardPerTokenStored: "0", // Not available from subgraph
    apr: aprFromBasisPoints,
  };
};

const transformUserActivity = (
  transactions: SubgraphStakingTransaction[],
  userAddress: string,
): StakingActivity => {
  // Transform subgraph transactions to local format
  const transformedTransactions: StakingTransaction[] = transactions.map(
    (tx) => ({
      id: tx.id,
      type: tx.type,
      amount: (parseFloat(tx.amount) / 1e18).toString(), // Convert from wei to CAMP
      timestamp: new Date(parseInt(tx.timestamp) * 1000),
      txHash: tx.transactionHash,
      status: "confirmed" as const, // All subgraph transactions are confirmed
      user: userAddress,
      vault: tx.vault
        ? {
            id: tx.vault.id,
            vaultAddress: tx.vault.vaultAddress,
            stakingToken: tx.vault.stakingToken,
          }
        : undefined,
    }),
  );

  const totalStaked = transformedTransactions
    .filter((tx) => tx.type === "stake")
    .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);

  const totalWithdrawn = transformedTransactions
    .filter((tx) => tx.type === "withdraw")
    .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);

  const totalRewardsClaimed = transformedTransactions
    .filter((tx) => tx.type === "claim")
    .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);

  return {
    transactions: transformedTransactions,
    totalTransactions: transformedTransactions.length,
    totalStaked: totalStaked.toString(),
    totalWithdrawn: totalWithdrawn.toString(),
    totalRewardsClaimed: totalRewardsClaimed.toString(),
  };
};

export const useStakingSubgraph = (
  pollingInterval: number = 10000,
  vaultId?: string,
): UseStakingSubgraphResult => {
  const { address } = useAccount();
  const [vaultStats, setVaultStats] = useState<VaultStats | null>(null);
  const [userStakingData, setUserStakingData] =
    useState<UserStakingData | null>(null);
  const [stakingActivity, setStakingActivity] = useState<StakingActivity>({
    transactions: [],
    totalTransactions: 0,
    totalStaked: "0",
    totalWithdrawn: "0",
    totalRewardsClaimed: "0",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);

  const fetchData = useCallback(
    async (isInitialLoad = false) => {
      try {
        setIsLoading(isInitialLoad);
        setError(null);

        // Fetch vault data from subgraph
        const vaultData = await getStakingVaultData(vaultId);

        const transformedVaultStats = transformVaultData(vaultData);

        setVaultStats(transformedVaultStats);

        if (address) {
          // Fetch user transactions from subgraph
          const userTransactions = await getUserStakingActivity(
            address,
            vaultId,
          );
          const userActivity = transformUserActivity(userTransactions, address);

          // Calculate user staking data from transactions
          const currentStake =
            userTransactions
              .filter((tx) => tx.type === "stake")
              .reduce((sum, tx) => sum + parseFloat(tx.amount) / 1e18, 0) -
            userTransactions
              .filter((tx) => tx.type === "withdraw")
              .reduce((sum, tx) => sum + parseFloat(tx.amount) / 1e18, 0);

          const earnedRewards = userTransactions
            .filter((tx) => tx.type === "claim")
            .reduce((sum, tx) => sum + parseFloat(tx.amount) / 1e18, 0);

          const userData: UserStakingData = {
            stakedBalance: currentStake.toString(),
            earnedRewards: "0", // Will be fetched from contract
            userRewardPerTokenPaid: "0",
            pendingRewards: "0",
          };

          setUserStakingData(userData);
          setStakingActivity(userActivity);
        } else {
          setUserStakingData(null);
          setStakingActivity({
            transactions: [],
            totalTransactions: 0,
            totalStaked: "0",
            totalWithdrawn: "0",
            totalRewardsClaimed: "0",
          });
        }

        setLastUpdated(new Date());
      } catch (err) {
        console.error("Failed to fetch staking data:", err);
        setError("Failed to load staking data. Please try again.");

        // Fallback to null/empty data on error
        setVaultStats(null);
        setUserStakingData(null);
        setStakingActivity({
          transactions: [],
          totalTransactions: 0,
          totalStaked: "0",
          totalWithdrawn: "0",
          totalRewardsClaimed: "0",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [address, vaultId],
  );

  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Initial load
    fetchData(isInitialLoadRef.current);
    isInitialLoadRef.current = false;

    // Set up polling
    if (pollingInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchData(false);
      }, pollingInterval);
    }

    setIsPolling(true);
  }, [fetchData, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Effect to manage polling lifecycle
  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  return {
    vaultStats,
    userStakingData,
    stakingActivity,
    isLoading,
    error,
    lastUpdated,
    isPolling,
    refetch,
  };
};
